import type { FaqItem } from "@/components/FaqSection";
import type { Lang } from "@/lib/content";

type Bi = Record<Lang, string>;

export interface LocationCity {
  slug: string;
  city: Bi;
  /** "iraq" cities show in the Iraq grid; "gulf" markets in the Gulf grid. */
  region: "iraq" | "gulf";
  metaTitle: Bi;
  title: Bi;
  description: Bi;
  /** BLUF intro. */
  intro: Bi;
  /** Dominant local sectors. */
  sectors: Bi[];
  /** ISO standard labels — linked to service pages. */
  standards: string[];
  /** Local office / coverage note. */
  office: Bi;
  /** schema.org areaServed for this location. */
  areaServed: Array<{ type: "Country" | "Place"; name: string }>;
  faq: FaqItem[];
  serviceType?: Bi;
  sources?: Array<{ href: string; label: Bi }>;
}

export const locations: LocationCity[] = [
  {
    slug: "baghdad",
    city: { en: "Baghdad", ar: "بغداد" },
    region: "iraq",
    areaServed: [
      { type: "Country", name: "Iraq" },
      { type: "Place", name: "Baghdad, Iraq" },
    ],
    metaTitle: {
      en: "ISO Certification in Baghdad, Iraq | IraqCert",
      ar: "شهادة الأيزو في بغداد، العراق | عراق سيرت",
    },
    title: { en: "ISO Certification in Baghdad", ar: "شهادة الأيزو في بغداد" },
    description: {
      en: "ISO certification preparation in Baghdad for government suppliers, construction and oil & gas. Certification scope and vendor requirements confirmed per enquiry.",
      ar: "التحضير لشهادات الآيزو في بغداد لموردي الحكومة والإنشاءات والنفط والغاز. نؤكد نطاق الشهادة ومتطلبات الموردين لكل طلب.",
    },
    intro: {
      en: "IraqCert supports companies in Baghdad from its operating base in Erbil preparing for ISO certification and vendor registration. The certification body conducts the certification audit and decides whether to issue a certificate. ISO 29001 is outside AQSR's disclosed UAF-accredited scope. IraqCert provides preparation support and confirms the issuing body and its accreditation scope before booking an ISO 29001 audit.",
      ar: "تدعم عراق سيرت الشركات في بغداد انطلاقاً من مقر عملها في أربيل في التحضير لشهادات الآيزو وتسجيل الموردين. وتجري جهة منح الشهادات تدقيق منح الشهادة وتتخذ قرار إصدارها. معيار ISO 29001 خارج نطاق اعتماد UAF المعلن لشركة AQSR. تقدم عراق سيرت دعم التحضير، وتؤكد الجهة المانحة ونطاق اعتمادها قبل حجز تدقيق ISO 29001.",
    },
    sectors: [
      { en: "Government & public sector", ar: "الحكومة والقطاع العام" },
      { en: "Federal oil & gas", ar: "النفط والغاز الاتحادي" },
      { en: "Construction & infrastructure", ar: "الإنشاءات والبنية التحتية" },
      { en: "Manufacturing", ar: "الصناعات التحويلية" },
    ],
    standards: ["ISO 9001", "ISO 45001", "ISO 14001", "ISO 29001"],
    office: {
      en: "We serve Baghdad companies from Erbil through calls, messages and agreed visits to client sites. Contact us to arrange the support you need.",
      ar: "نخدم شركات بغداد من أربيل عبر المكالمات والرسائل والزيارات المتفق عليها إلى مواقع العملاء. تواصل معنا لترتيب الدعم الذي تحتاجه.",
    },
    faq: [
      {
        q: {
          en: "How do I get ISO certified in Baghdad?",
          ar: "كيف أحصل على شهادة الأيزو في بغداد؟",
        },
        a: {
          en: "Share your activity, sites and the buyer’s requirement. IraqCert helps review gaps, prepare documentation and coordinate assessment. Confirm the proposed certification body, standard and relevant accreditation scope before proceeding; that body makes the certification decision.",
          ar: "شارك نشاط شركتك ومواقعها ومتطلب المشتري. تساعد عراق سيرت في مراجعة الفجوات وإعداد الوثائق وتنسيق التقييم. أكّد الجهة المانحة المقترحة والمواصفة ونطاق اعتمادها ذي الصلة قبل المتابعة؛ فالجهة المانحة هي التي تتخذ قرار منح الشهادة.",
        },
      },
      {
        q: {
          en: "Which ISO standards do Baghdad government tenders require?",
          ar: "أي معايير أيزو تشترطها المناقصات الحكومية في بغداد؟",
        },
        a: {
          en: "Check the actual tender documents. The standard, scope and any accreditation requirement depend on the buyer and contract. Do not assume ISO 9001, ISO 14001, ISO 45001 or ISO 29001 is required for every tender. Send us the stated requirement so we can scope the support you need.",
          ar: "راجع وثائق المناقصة نفسها. تختلف المواصفة والنطاق وأي متطلبات للاعتماد باختلاف المشتري والعقد. لا تفترض اشتراط ISO 9001 أو ISO 14001 أو ISO 45001 أو ISO 29001 في كل مناقصة. أرسل لنا نص المتطلب لنحدّد الدعم الذي تحتاجه.",
        },
      },
      {
        q: {
          en: "How does IraqCert serve companies in Baghdad?",
          ar: "كيف تخدم عراق سيرت الشركات في بغداد؟",
        },
        a: {
          en: "Our operating base is in Erbil. We support Baghdad companies remotely and through agreed client-site visits; we do not have a Baghdad office.",
          ar: "مقر عملنا في أربيل. ندعم شركات بغداد عن بُعد ومن خلال زيارات متفق عليها إلى مواقع العملاء؛ وليس لدينا مكتب في بغداد.",
        },
      },
    ],
  },
  {
    slug: "basra",
    city: { en: "Basra", ar: "البصرة" },
    region: "iraq",
    areaServed: [
      { type: "Country", name: "Iraq" },
      { type: "Place", name: "Basra, Iraq" },
    ],
    metaTitle: {
      en: "ISO Certification in Basra, Iraq | IraqCert",
      ar: "شهادة الأيزو في البصرة، العراق | عراق سيرت",
    },
    title: { en: "ISO Certification in Basra", ar: "شهادة الأيزو في البصرة" },
    description: {
      en: "ISO certification preparation in Basra and vendor-registration support for Basra Oil Company and Rumaila. Issuing body and scope confirmed per enquiry.",
      ar: "التحضير لشهادات الآيزو في البصرة ودعم تسجيل الموردين لدى شركة نفط البصرة والرميلة. نؤكد الجهة المانحة ونطاق الشهادة لكل طلب.",
    },
    intro: {
      en: "IraqCert helps Basra companies build management systems, prepare for ISO audits and assemble supplier-registration submissions for their intended oil-and-gas buyers. The independent certification body audits the system and decides whether to issue a certificate; each buyer decides supplier acceptance. ISO 29001 is outside AQSR's disclosed UAF-accredited scope. IraqCert provides preparation support and confirms the issuing body and its accreditation scope before booking an ISO 29001 audit.",
      ar: "تساعد عراق سيرت شركات البصرة في بناء أنظمة الإدارة والتحضير لتدقيق الأيزو وتجهيز طلبات تسجيل الموردين لدى مشتري النفط والغاز المستهدفين. تدقق جهة منح الشهادات المستقلة النظام وتتخذ قرار إصدار الشهادة؛ ويقرر كل مشترٍ قبول المورد. معيار ISO 29001 خارج نطاق اعتماد UAF المعلن لشركة AQSR. تقدم عراق سيرت دعم التحضير، وتؤكد الجهة المانحة ونطاق اعتمادها قبل حجز تدقيق ISO 29001.",
    },
    sectors: [
      { en: "Oil & gas", ar: "النفط والغاز" },
      { en: "Petrochemicals", ar: "البتروكيماويات" },
      { en: "Logistics & ports", ar: "اللوجستيات والموانئ" },
      { en: "Construction", ar: "الإنشاءات" },
    ],
    standards: ["ISO 29001", "ISO 9001", "ISO 45001", "ISO 14001"],
    office: {
      en: "From our Erbil base, we support Basra companies remotely and through agreed client-site visits, including vendor-registration support for their intended buyers.",
      ar: "ندعم شركات البصرة من مقر عملنا في أربيل عن بُعد ومن خلال زيارات متفق عليها إلى مواقع العملاء، بما في ذلك دعم تسجيل الموردين لدى الجهات التي يستهدفونها.",
    },
    faq: [
      {
        q: {
          en: "How do I get ISO certified in Basra?",
          ar: "كيف أحصل على شهادة الأيزو في البصرة؟",
        },
        a: {
          en: "We review your buyer’s requirement, prepare the management system with your team and coordinate with the proposed certification body. Confirm the body’s accreditation for the specific standard and scope before booking. That body conducts the audit and makes the certification decision.",
          ar: "نراجع متطلب المشتري ونُهيّئ نظام الإدارة مع فريقك وننسّق مع جهة منح الشهادات المقترحة. تحقق من اعتماد الجهة للمواصفة والنطاق المحددين قبل الحجز. وتجري تلك الجهة التدقيق وتتخذ قرار منح الشهادة.",
        },
      },
      {
        q: {
          en: "Which ISO standards do oil and gas vendors in Basra need?",
          ar: "أي معايير أيزو يحتاجها موردو النفط والغاز في البصرة؟",
        },
        a: {
          en: "ISO 9001 addresses quality, ISO 14001 environmental management and ISO 45001 occupational health and safety. ISO 29001 concerns petroleum-sector quality. The required combination depends on the operator, supplier category and current tender; do not assume every buyer requires all four.",
          ar: "يتناول ISO 9001 الجودة، وISO 14001 الإدارة البيئية، وISO 45001 الصحة والسلامة المهنية. ويتعلق ISO 29001 بجودة قطاع البترول. تعتمد المواصفات المطلوبة على المشغل وفئة المورد والمناقصة الحالية؛ لا تفترض أن كل مشترٍ يشترط الأربع جميعاً.",
        },
      },
      {
        q: {
          en: "Do you handle Basra Oil Company vendor registration?",
          ar: "هل تتولون تسجيل الموردين في شركة نفط البصرة؟",
        },
        a: {
          en: "Yes. IraqCert manages supplier registration with Basra Oil Company, Basrah Gas Company, and Rumaila alongside the ISO certificates the process requires.",
          ar: "نعم. يتولى عراق سيرت تسجيل الموردين لدى شركة نفط البصرة وغاز البصرة والرميلة إلى جانب شهادات الأيزو التي تتطلبها العملية.",
        },
      },
    ],
  },
  {
    slug: "erbil",
    city: { en: "Erbil", ar: "أربيل" },
    region: "iraq",
    areaServed: [
      { type: "Country", name: "Iraq" },
      { type: "Place", name: "Erbil, Kurdistan Region of Iraq" },
    ],
    metaTitle: {
      en: "ISO Certification in Erbil, Kurdistan | IraqCert",
      ar: "شهادة الأيزو في أربيل، كردستان | عراق سيرت",
    },
    title: {
      en: "ISO Certification in Erbil (Kurdistan)",
      ar: "شهادة الأيزو في أربيل (كردستان)",
    },
    description: {
      en: "ISO preparation and management-system support in Erbil and the Kurdistan Region. Confirm the required standard, issuing body and scope before booking.",
      ar: "دعم التحضير للأيزو وأنظمة الإدارة في أربيل وإقليم كردستان. تحقق من المواصفة المطلوبة والجهة المانحة والنطاق قبل الحجز.",
    },
    intro: {
      en: "Our Erbil-based team works with companies in the Kurdistan Region on management systems, ISO preparation and supplier readiness. We align support with your actual activities, locations and buyer requirements. An independent certification body conducts the certification audit and decides whether to issue the certificate. Certification alone does not guarantee tender qualification or a contract.",
      ar: "يعمل فريق عراق سيرت في أربيل مع شركات إقليم كردستان على أنظمة الإدارة والتحضير للأيزو وجاهزية الموردين. نطابق الدعم مع أنشطتك ومواقعك الفعلية ومتطلبات المشتري. وتجري جهة منح شهادات مستقلة تدقيق منح الشهادة وتتخذ قرار إصدارها. ولا تضمن الشهادة وحدها التأهل للمناقصة أو الحصول على عقد.",
    },
    sectors: [
      { en: "Construction", ar: "الإنشاءات" },
      { en: "Technology & IT", ar: "التكنولوجيا وتقنية المعلومات" },
      { en: "Manufacturing", ar: "الصناعات التحويلية" },
      { en: "Oilfield services (KRI)", ar: "خدمات الحقول النفطية (الإقليم)" },
    ],
    standards: ["ISO 9001", "ISO 45001", "ISO 14001", "ISO 27001"],
    office: {
      en: "Our operating base is in Erbil. We serve clients across Iraq by phone, messages and agreed visits to their sites. Please contact us first; we do not currently receive walk-in visitors.",
      ar: "مقر عملنا في أربيل. نخدم العملاء في أنحاء العراق عبر الهاتف والرسائل والزيارات المتفق عليها إلى مواقعهم. يرجى التواصل معنا أولاً؛ مقرنا غير مفتوح حالياً لاستقبال العملاء.",
    },
    faq: [
      {
        q: {
          en: "How do I get ISO certified in Erbil?",
          ar: "كيف أحصل على شهادة الأيزو في أربيل؟",
        },
        a: {
          en: "We review gaps, develop practical procedures and records with your team, and coordinate certification arrangements. The proposed independent certification body conducts the audit and makes the certification decision. Confirm its accreditation scope and your buyer’s requirements first.",
          ar: "نراجع الفجوات ونطور الإجراءات والسجلات العملية مع فريقك وننسّق ترتيبات منح الشهادة. وتجري جهة منح الشهادات المستقلة المقترحة التدقيق وتتخذ قرار منح الشهادة. تحقق أولاً من نطاق اعتمادها ومتطلبات المشتري.",
        },
      },
      {
        q: {
          en: "Is ISO certification recognized in Kurdistan?",
          ar: "هل شهادة الأيزو معتمدة في كردستان؟",
        },
        a: {
          en: "Acceptance depends on the buyer’s stated standard, scope and accreditation conditions. Check those conditions and the proposed issuing body before committing. ISO certification does not replace company registration or guarantee acceptance for every tender.",
          ar: "يعتمد القبول على المواصفة والنطاق وشروط الاعتماد التي يحددها المشتري. تحقق من هذه الشروط والجهة المانحة المقترحة قبل الالتزام. لا تحل شهادة الأيزو محل تسجيل الشركة ولا تضمن القبول في جميع المناقصات.",
        },
      },
      {
        q: { en: "Can I visit IraqCert in Erbil?", ar: "هل يمكنني زيارة عراق سيرت في أربيل؟" },
        a: {
          en: "Please contact us by phone or WhatsApp first. Our work is based in Erbil, with remote support and agreed visits to client sites. Our premises are not currently open for walk-in visits.",
          ar: "يرجى التواصل معنا أولاً عبر الهاتف أو واتساب. نعمل من أربيل ونقدّم الدعم عن بُعد ونرتّب الزيارات إلى مواقع العملاء. مقرنا غير مفتوح حالياً لاستقبال الزيارات المباشرة.",
        },
      },
    ],
  },
  {
    slug: "uae",
    city: {
      en: "UAE",
      ar: "الإمارات",
    },
    region: "gulf",
    areaServed: [
      {
        type: "Country",
        name: "United Arab Emirates",
      },
      {
        type: "Place",
        name: "Dubai, United Arab Emirates",
      },
      {
        type: "Place",
        name: "Abu Dhabi, United Arab Emirates",
      },
    ],
    metaTitle: {
      en: "ISO Certification Preparation in the UAE | IraqCert",
      ar: "التحضير لشهادات الأيزو في الإمارات | عراق سيرت",
    },
    title: {
      en: "ISO Certification Preparation in the UAE",
      ar: "التحضير لشهادات الأيزو في الإمارات",
    },
    description: {
      en: "Prepare your company’s ISO certification scope and buyer evidence for the UAE. IraqCert supports preparation and coordination; an independent body decides certification.",
      ar: "حدّد نطاق شهادة الأيزو والأدلة التي يطلبها المشتري في الإمارات. تدعم عراق سيرت التحضير والتنسيق، وتتخذ جهة منح مستقلة قرار إصدار الشهادة.",
    },
    intro: {
      en: "Start with the buyer’s written requirement, your company’s activities and the sites to be covered. IraqCert helps review the intended scope, prepare documentation and coordinate the agreed assessment process. The independent certification body conducts the certification audit and makes the certification decision. Confirm its relevant accreditation scope and the buyer’s acceptance conditions before committing. A management-system certificate does not replace supplier registration or prequalification, and does not guarantee a tender award.",
      ar: "ابدأ بمتطلب المشتري المكتوب، وأنشطة شركتك، والمواقع المطلوب إدراجها في نطاق الشهادة. تساعد عراق سيرت في مراجعة النطاق المقترح وإعداد الوثائق وتنسيق إجراءات التقييم المتفق عليها. وتتولى جهة منح الشهادات المستقلة تدقيق منح الشهادة وتتخذ قرار إصدارها. تحقّق من نطاق اعتمادها ذي الصلة وشروط قبول المشتري قبل الالتزام. ولا تُغني شهادة نظام الإدارة عن تسجيل المورد أو تأهيله المسبق، ولا تعني ترسية المناقصة عليه.",
    },
    sectors: [
      {
        en: "Oil & Gas",
        ar: "النفط والغاز",
      },
      {
        en: "Construction",
        ar: "الإنشاءات",
      },
      {
        en: "Food & Beverage",
        ar: "الأغذية والمشروبات",
      },
      {
        en: "Logistics & Ports",
        ar: "الخدمات اللوجستية والموانئ",
      },
    ],
    standards: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 22000"],
    office: {
      en: "We discuss support for your UAE requirement from our base in Erbil. We do not have a UAE office. Delivery arrangements and the proposed independent certification body are confirmed for each engagement.",
      ar: "نناقش الدعم المطلوب لمتطلبات شركتك في الإمارات انطلاقاً من مقرّنا في أربيل، وليس لدينا مكتب في الإمارات. ونؤكد ترتيبات تقديم الخدمة وجهة منح الشهادات المستقلة المقترحة لكل تعاقد.",
    },
    faq: [
      {
        q: {
          en: "Do I need an EIAC-accredited certificate for a UAE tender?",
          ar: "هل أحتاج إلى شهادة صادرة عن جهة معتمدة من EIAC لمناقصة في الإمارات؟",
        },
        a: {
          en: "Read the actual tender’s standard, activity/site scope and accreditation clause. If it specifies EIAC accreditation, check the proposed certification body’s relevant scope in the current EIAC records and confirm acceptance with the buyer. Do not assume that an accreditation mark alone satisfies every tender or free-zone requirement.",
          ar: "راجع المواصفة المطلوبة ونطاق الأنشطة والمواقع وبند الاعتماد في وثائق المناقصة نفسها. إذا اشترطت اعتماد EIAC، فتحقّق من نطاق اعتماد جهة منح الشهادات المقترحة في سجلات EIAC الحالية، وأكّد قبولها مع المشتري. ولا تفترض أن وجود علامة اعتماد وحده يفي بمتطلبات كل مناقصة أو منطقة حرة.",
        },
      },
      {
        q: {
          en: "Who issues the certificate, and how is buyer acceptance checked?",
          ar: "من يصدر الشهادة، وكيف أتحقّق من قبول المشتري لها؟",
        },
        a: {
          en: "An independent certification body issues the certificate following its assessment and certification decision. IraqCert provides preparation and coordination support. Before booking, check the named issuing body, standard, certified activities and sites, accreditation scope and the buyer’s written conditions. Acceptance for one buyer or contract does not establish acceptance for another in the UAE or Saudi Arabia.",
          ar: "تصدر جهة منح شهادات مستقلة الشهادة بعد التقييم واتخاذ قرار المنح. وتقدّم عراق سيرت دعم التحضير والتنسيق. قبل الحجز، راجع اسم الجهة المانحة والمواصفة والأنشطة والمواقع المشمولة بالشهادة ونطاق الاعتماد وشروط المشتري المكتوبة. ولا يثبت قبول الشهادة لدى مشترٍ أو لعقد معين قبولها لدى مشترٍ آخر أو لعقد آخر في الإمارات أو السعودية.",
        },
      },
      {
        q: {
          en: "Which ISO certificates should we prepare for an ADNOC enquiry?",
          ar: "ما شهادات الأيزو التي ينبغي تجهيزها عند التقدّم إلى أدنوك؟",
        },
        a: {
          en: "Check the current ADNOC prequalification information for your product or service and the documents for the specific tender. Do not assume one ISO checklist applies to every supplier activity. In-Country Value (ICV) is separate from management-system certification. Send the stated buyer requirement so the preparation scope can be reviewed; registration, qualification and award remain separate decisions.",
          ar: "راجع معلومات التأهيل المسبق الحالية لدى أدنوك للمنتج أو الخدمة التي تقدّمها، ووثائق المناقصة المحددة. ولا تفترض وجود قائمة واحدة من شهادات الأيزو تنطبق على جميع أنشطة الموردين. فالقيمة المحلية المضافة (ICV) مسار منفصل عن شهادات أنظمة الإدارة. أرسل متطلب المشتري المكتوب لمراجعة نطاق التحضير؛ فالتسجيل والتأهيل وترسية العقد قرارات منفصلة.",
        },
      },
    ],
    sources: [
      {
        href: "https://www.iso.org/certification.html",
        label: {
          en: "ISO: certification and certification bodies",
          ar: "ISO: الشهادات وجهات منحها",
        },
      },
      {
        href: "https://eiac.gov.ae/services/certification-bodies/",
        label: {
          en: "EIAC: accreditation of certification bodies",
          ar: "EIAC: اعتماد جهات منح الشهادات",
        },
      },
      {
        href: "https://supplierhub.adnoc.ae/services/prequalification",
        label: {
          en: "ADNOC Supplier Hub: prequalification",
          ar: "بوابة موردي أدنوك: التأهيل المسبق",
        },
      },
    ],
    serviceType: {
      en: "ISO certification preparation and coordination",
      ar: "التحضير لشهادات الأيزو وتنسيق إجراءاتها",
    },
  },
  {
    slug: "saudi-arabia",
    city: { en: "Saudi Arabia", ar: "السعودية" },
    region: "gulf",
    areaServed: [
      { type: "Country", name: "Saudi Arabia" },
      { type: "Place", name: "Riyadh, Saudi Arabia" },
    ],
    metaTitle: {
      en: "ISO Certification Support in Saudi Arabia | IraqCert",
      ar: "دعم شهادات الأيزو في السعودية | عراق سيرت",
    },
    title: {
      en: "ISO Certification in Saudi Arabia",
      ar: "شهادة ايزو في المملكة العربية السعودية",
    },
    description: {
      en: "ISO preparation support for companies serving Saudi buyers. Review the required standard, proposed certification body and scope before requesting a quotation.",
      ar: "دعم التحضير لشهادات الأيزو للشركات التي تتعامل مع مشترين في السعودية. راجع المواصفة المطلوبة والجهة المانحة المقترحة والنطاق قبل طلب عرض سعر.",
    },
    intro: {
      en: "Start with the Saudi buyer’s stated requirement: the standard, activities and sites to be covered, deadline and any accreditation condition. IraqCert helps review the requirement, prepare your management system and coordinate with the proposed certification body. That body determines its assessment arrangements and makes the certification decision. Certification does not itself secure supplier registration, tender qualification or a contract, and acceptance by one buyer does not establish acceptance by another.",
      ar: "ابدأ بمتطلب المشتري في السعودية: المواصفة والأنشطة والمواقع المطلوب شمولها والموعد النهائي وأي شرط للاعتماد. تساعد عراق سيرت في مراجعة المتطلب والتحضير لنظام الإدارة والتنسيق مع جهة منح الشهادات المقترحة. تحدّد تلك الجهة ترتيبات تقييمها وتتخذ قرار منح الشهادة. ولا تضمن الشهادة وحدها تسجيل المورد أو التأهل للمناقصة أو الحصول على عقد، كما أن قبولها لدى مشترٍ لا يعني قبولها لدى غيره.",
    },
    sectors: [
      { en: "Oil & Gas", ar: "النفط والغاز" },
      { en: "Construction & Infrastructure", ar: "البناء والبنية التحتية" },
      { en: "Manufacturing", ar: "التصنيع" },
      { en: "Food & Beverage", ar: "الأغذية والمشروبات" },
    ],
    standards: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 29001"],
    office: {
      en: "IraqCert supports companies serving Saudi Arabia, including Riyadh, Dammam and Jeddah. Confirm the delivery arrangements and proposed certification body’s assessment requirements when agreeing the scope.",
      ar: "تدعم عراق سيرت الشركات التي تخدم السوق السعودية، بما فيها الرياض والدمام وجدة. أكّد ترتيبات تقديم الخدمة ومتطلبات التقييم لدى جهة منح الشهادات المقترحة عند الاتفاق على النطاق.",
    },
    faq: [
      {
        q: {
          en: "Is ISO certification required for Saudi government tenders?",
          ar: "هل شهادة الأيزو مطلوبة للمناقصات الحكومية السعودية؟",
        },
        a: {
          en: "Check the specific tender documents and qualification criteria. Requirements differ by buyer and contract; do not assume one standard applies to every tender. Share the relevant clause so the proposed service can address the actual requirement.",
          ar: "راجع وثائق المناقصة ومعايير التأهيل الخاصة بها. تختلف المتطلبات باختلاف الجهة المشترية والعقد؛ فلا تفترض اشتراط مواصفة واحدة في جميع المناقصات. شارك البند المعني لتحديد الخدمة وفق المتطلب الفعلي.",
        },
      },
      {
        q: {
          en: "Will my ISO certificate be accepted for Saudi Aramco registration?",
          ar: "هل تُقبل شهادة الأيزو الخاصة بشركتي للتسجيل لدى أرامكو السعودية؟",
        },
        a: {
          en: "Confirm the current requirements for your supplier category directly with Aramco before commissioning certification. Check the standard, activity scope, issuing body and any accreditation requirement. A certificate alone does not guarantee registration, approval or work with Aramco.",
          ar: "أكّد متطلبات فئة الموردين التي تنتمي إليها شركتك مباشرةً مع أرامكو قبل طلب خدمة الشهادة. تحقّق من المواصفة ونطاق الأنشطة والجهة المانحة وأي شرط للاعتماد. لا تضمن الشهادة وحدها التسجيل أو القبول أو الحصول على أعمال مع أرامكو.",
        },
      },
      {
        q: {
          en: "How do I check the accreditation requirement?",
          ar: "كيف أتحقّق من شرط الاعتماد؟",
        },
        a: {
          en: "Ask the buyer for the exact acceptance clause, then verify the proposed certification body’s relevant scope with its accreditation body. If the buyer specifies an accreditation or issuing-body requirement, confirm that the proposed route meets it before proceeding. ISO 29001 support must be scoped separately; do not assume it falls within another standard’s accreditation scope.",
          ar: "اطلب من المشتري نص شرط القبول، ثم تحقّق لدى هيئة الاعتماد من نطاق اعتماد جهة منح الشهادات المقترحة ذي الصلة. إذا حدّد المشتري شرطاً للاعتماد أو للجهة المانحة، فأكّد استيفاء المسار المقترح له قبل المتابعة. ويجب تحديد نطاق دعم ISO 29001 بصورة مستقلة؛ فلا تفترض شموله بنطاق اعتماد مواصفة أخرى.",
        },
      },
    ],
  },
];

export const locationMap: Record<string, LocationCity> = Object.fromEntries(
  locations.map((l) => [l.slug, l]),
);
export const locationSlugs = locations.map((l) => l.slug);
export function getLocation(slug: string): LocationCity | undefined {
  return locationMap[slug];
}
